/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import ReactDOM from "react-dom";
import {VerificationStatusResponse, VerificationStatusResult} from "types/responses/VerificationStatusResponse";
import BookingAction from "../store/booking/BookingAction";
import {DEFAULT_HALL, DEFAULT_HALL_ID, DEFAULT_RESERVATION_DAYS_FROM_NOW, IS_UPSELL_ALLOWED} from "helpers/config";
import {PackagesCart} from "types/entities/PackagesCart";
import {useDispatch} from "useDispatch";
import {getNewStep, getNewStepGift} from "./utility";
import {VerificationType} from "types/enums/VerificationType";
import {PackagesCartItemSlot} from "types/entities/PackagesCartItemSlot";
import {PackageCategory} from "types/enums/PackageCategory";
import {HallCode} from "types/enums/HallCode";
import {AnyObject, ISchema, ObjectSchema, Reference} from "yup";
import {BookingNavigationStep} from "store/booking/BookingReducer";
import {useTypedSelector} from "helpers/reducers";
import {useEffect, useRef} from "react";
import Dates from "utils/Dates";
import {getVoucherCodes} from "utils/voucherUtils";
import {getQueryParams} from "helpers/helpers";
import {useHistory} from "react-router";
import {useHallOpening} from "utils/hooks/useHallOpening";
import {useDebouncedDispatch} from "utils/useDebounceQueue";
import {RouteName} from "utils/hooks/useNavigation";

export const useValueVerification = (packagesCart: PackagesCart) => {
    const dispatch = useDispatch();

    const sendVerification = (value, type: VerificationType): Promise<VerificationStatusResponse> => {
        return new Promise((resolve, reject) => {
            dispatch(
                BookingAction.fetchVerification(
                    {
                        value: value,
                        hall_id: DEFAULT_HALL_ID,
                        type,
                    },
                    packagesCart as PackagesCart
                )
            )
                .then(r => {
                    resolve(r);
                })
                .catch(err => {
                    reject(err);
                });
        });
    };

    const createContainer = () => {
        const container = document.createElement("div");
        const holder = document.getElementById("verification-code-container") as HTMLElement;
        holder.appendChild(container);
        return container;
    };

    const handleVerification = (
        data_2: {request_id: string; verification_code: string; type: VerificationType},
        resultVerification: VerificationStatusResponse
    ): Promise<VerificationStatusResult> => {
        return new Promise((resolve, reject) => {
            if (data_2.request_id === resultVerification.verification_request_id) {
                dispatch(BookingAction.fetchSubmitVerificationCode(data_2, packagesCart as PackagesCart)).then(r_1 => {
                    if (r_1.result) {
                        return resolve(r_1);
                    } else {
                        return reject(false);
                    }
                });
            } else {
                return reject(false);
            }
        });
    };

    const closeVerificationWindow = container => {
        // eslint-disable-next-line react/no-deprecated
        ReactDOM.unmountComponentAtNode(container);
        container.remove();
    };

    return {
        sendVerification,
        createContainer,
        handleVerification,
        closeVerificationWindow,
    };
};

export const useValueVerificationForShop = () => {
    const dispatch = useDispatch();

    const sendVerification = (value, type: VerificationType): Promise<VerificationStatusResponse> => {
        return new Promise((resolve, reject) => {
            dispatch(
                BookingAction.fetchVerificationForShop({
                    value: value,
                    hall_id: DEFAULT_HALL_ID,
                    type,
                })
            )
                .then(r => {
                    resolve(r);
                })
                .catch(err => {
                    reject(err);
                });
        });
    };

    const createContainer = () => {
        const container = document.createElement("div");
        const holder = document.getElementById("verification-code-container") as HTMLElement;
        holder.appendChild(container);
        return container;
    };

    const handleVerification = (
        data_2: {request_id: string; verification_code: string; type: VerificationType},
        resultVerification: VerificationStatusResponse
    ): Promise<boolean> => {
        return new Promise((resolve, reject) => {
            if (data_2.request_id === resultVerification.verification_request_id) {
                dispatch(BookingAction.fetchSubmitVerificationCodeForShop(data_2)).then(r_1 => {
                    if (r_1.result) {
                        return resolve(true);
                    } else {
                        return reject(false);
                    }
                });
            } else {
                return reject(false);
            }
        });
    };

    const closeVerificationWindow = container => {
        // eslint-disable-next-line react/no-deprecated
        ReactDOM.unmountComponentAtNode(container);
        container.remove();
    };

    return {
        sendVerification,
        createContainer,
        handleVerification,
        closeVerificationWindow,
    };
};
/**
 * Filters and reduces the `packagesCartItemSlots` array based on the `selectedPackage` and returns the result.
 * @param {PackagesCartItemSlot[]} packagesCartItemSlots - The array of packages cart item slots.
 * @param {PackageCategory} packageCategory - The selected package object.
 * @returns {PackagesCartItemSlot[]} - The filtered and reduced array of packages cart item slots.
 */
export const itemSlots: (packagesCartItemSlots: PackagesCartItemSlot[], packageCategory: PackageCategory) => PackagesCartItemSlot[] = (
    packagesCartItemSlots: PackagesCartItemSlot[],
    packageCategory: PackageCategory
): PackagesCartItemSlot[] => {
    return packagesCartItemSlots
        .filter((itemSlot, index) => (packageCategory === PackageCategory.Event && DEFAULT_HALL !== HallCode.Zwolle ? index === 0 : true))
        .reduce((acc: PackagesCartItemSlot[], itemSlot) => {
            const existingSlot = acc.find(slot => slot.timestamp === itemSlot.timestamp);
            if (existingSlot) {
                existingSlot.persons_count += itemSlot.persons_count;
            } else {
                acc.push({...itemSlot});
            }
            return acc;
        }, []);
};

/**
 * Checks if a field is required in a given schema.
 *
 * @param {ObjectSchema} schema - The schema to check.
 * @returns {Object} - An object containing a function named `testField`.
 */
export const useRequiredField = (schema: ObjectSchema<any, AnyObject, any, "">) => {
    const testField = fieldName => {
        const field: Reference<unknown> | ISchema<any, AnyObject, any, any> = schema.fields[fieldName];

        if (field) {
            // @ts-ignore
            if (!field.tests) {
                return false;
            }

            // @ts-ignore
            const hasRequiredTest = field.tests.some(test => {
                return test.OPTIONS.name === "required";
            });

            if (hasRequiredTest) {
                // Field is required
                return true;
            } else {
                // Field exists but is not required
                return false;
            }
        } else {
            // Field does not exist in the schema
            return false;
        }
    };

    return {
        testField,
    };
};

/**
 * Helper function for navigating through the booking steps.
 * @returns:
 *      @proceedIfTimeslotsAllowed: Sets the current step of the booking process based on the given location.
 *      @proceedIfTimeslotsNotAllowed: Sets the current step of the booking process if timeslots not allowed
 *      @updateSteps: Updates the steps based on the provided pathname
 */
export const useNavigationSteps = () => {
    const dispatch = useDispatch();
    const bookingState = useTypedSelector(state => state.bookingState);

    const setCurrentStep = (step: BookingNavigationStep) => {
        dispatch(BookingAction.setCurrentNavigationStep(step));
    };

    /**
     * Sets the current step of the booking process based on the given location.
     *
     * @param {RouteName} routeName - The location to determine the current step.
     *
     * @returns {void}
     */
    const proceedIfTimeslotsAllowed = (routeName: RouteName): void => {
        const availablePackages = bookingState?.bookingProcess?.availablePackageItems;
        const availablePackagesHasUpsell = availablePackages ? availablePackages.upsells.length > 0 : false;
        switch (routeName) {
            case RouteName.Main:
                return setCurrentStep(getNewStep(bookingState, 1));
            case RouteName.Options:
                return setCurrentStep(getNewStep(bookingState, 2));
            case RouteName.TimeslotsNew:
                return setCurrentStep(getNewStep(bookingState, 3));
            case RouteName.Packages:
                return setCurrentStep(getNewStep(bookingState, 4));
            case RouteName.UpsellsNew:
                return setCurrentStep(getNewStep(bookingState, 5));
            case RouteName.Customer:
                return setCurrentStep(getNewStep(bookingState, IS_UPSELL_ALLOWED ? (availablePackagesHasUpsell ? 6 : 5) : 5));
            case RouteName.Paid:
                return setCurrentStep(getNewStep(bookingState, IS_UPSELL_ALLOWED ? (availablePackagesHasUpsell ? 7 : 6) : 6));
            case RouteName.Gift:
                return setCurrentStep(getNewStepGift(bookingState, 2));
            case RouteName.GiftCheckout:
                return setCurrentStep(getNewStepGift(bookingState, 3));
        }
    };

    /**
     * Executes a specific action based on the provided location.
     * @param {string} location - The location to check.
     * @returns {void}
     */
    const proceedIfTimeslotsNotAllowed = (location: string): void => {
        if (location.includes("package")) {
            setCurrentStep(getNewStep(bookingState, 1));
        } else if (location.includes("upsell")) {
            setCurrentStep(getNewStep(bookingState, 2));
        } else if (location.includes("customer")) {
            setCurrentStep(getNewStep(bookingState, 3));
        } else {
        }
    };

    /**
     * Updates the steps based on the provided pathname.
     *
     * @param {string} pathName - The pathname to determine the step update.
     */
    const updateSteps = (pathName: RouteName) => {
        return bookingState.timeslotsAllowed ? proceedIfTimeslotsAllowed(pathName) : proceedIfTimeslotsNotAllowed(pathName);
    };

    return {
        proceedIfTimeslotsAllowed,
        proceedIfTimeslotsNotAllowed,
        updateSteps,
    };
};

/**
 * Method to handle query parameters for the booking process.
 *
 * @return {Object} An object with four methods:
 *   - changeQueryParams: Changes and updates the query parameters based on the current filter state.
 *   - changeQueryParamsIfUpdated: Checks if the query parameters have been updated and updates the URL accordingly.
 *   - fetchPackagesByLocation: Fetches packages based on the current location and filter state.
 *   - setFilterFromQueryParams: Sets the filter state based on the query parameters.
 */
export const useQueryParams = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const hallOpening = useHallOpening();

    const bookingState = useTypedSelector(state => state.bookingState);
    const vouchers = useTypedSelector(state => state.voucherState.voucherItems);
    const bookingFilter = bookingState.bookingProcess.filter;
    const dateChosenByCustomer = bookingState.bookingProcess.dateChosenByCustomer;

    const queryParams = new URLSearchParams(location.search as any);
    const currentRouteParams = useRef(queryParams.toString());
    const {debouncedDispatch} = useDebouncedDispatch();

    const fetchPackagesByLocation = () => {
        if (!location.pathname.includes("customer") && !location.pathname.includes("booking/book")) {
            const voucherCodes = getVoucherCodes(vouchers);
            return debouncedDispatch(BookingAction.fetchPackagesByLocationParams(location, bookingFilter, dateChosenByCustomer, voucherCodes));
        }
    };

    const fetchPackagesForHall = () => {
        const date = Dates.ymd(bookingFilter.date) || Dates.ymd(new Date());
        return debouncedDispatch(BookingAction.fetchPackagesForHall(date));
    };

    const historyFromCurrentRouteParams = () => {
        const currentRouteParams: any = Object.fromEntries(queryParams.entries());

        if (!Dates.isToday(new Date(bookingFilter.date)) || !Dates.isToday(new Date(currentRouteParams.date))) {
            const filterParams = changeQueryParams();

            const routeParams = new URLSearchParams(filterParams as any).toString();
            const path = location.pathname.includes("booking/book") ? "/booking" : location.pathname;
            history.push(path + "?" + routeParams);
        }
    };

    const setFilterFromQueryParams = () => {
        if (getQueryParams()) {
            dispatch(
                BookingAction.setFilter({
                    ...bookingFilter,
                    ...getQueryParams(),
                })
            );
        } else {
            //resetPackagesParams();
        }
    };

    const changeQueryParamsIfUpdated = () => {
        const routeParams = new URLSearchParams(bookingFilter as any).toString();

        if (currentRouteParams.current === routeParams) {
            return;
        }

        currentRouteParams.current = routeParams;
        // const path = location.pathname.includes("booking/book") ? "/booking" : location.pathname;
        const path = location.pathname;
        history.replace(path + "?" + routeParams);
    };

    useEffect(() => {
        historyFromCurrentRouteParams();
    }, []);

    useEffect(() => {
        setFilterFromQueryParams();
    }, []);

    useEffect(() => {
        // dispatch(BookingAction.fetchPackagesByLocationParams(location));
        changeQueryParamsIfUpdated();
    }, [bookingFilter]);

    const changeQueryParams = () => {
        // const minimalDatePreset =
        //     hallOpening && hallOpening.hallOpeningDefined && hallOpening.hallOpeningDate
        //         ? new Date(hallOpening.hallOpeningDate)
        //         : new Date(Date.now() + DEFAULT_RESERVATION_DAYS_FROM_NOW * 24 * 60 * 60 * 1000);

        const minimalDatePreset = new Date(Date.now() + DEFAULT_RESERVATION_DAYS_FROM_NOW * 24 * 60 * 60 * 1000);
        const queryParams = new URLSearchParams(location.search);
        const filterParams: any = Object.fromEntries(queryParams.entries());

        if (!filterParams) {
            return;
        }

        const isFilterDate = filterParams.date;
        const isOldDate = typeof isFilterDate === "undefined" ? true : new Date(filterParams.date).getTime() < new Date(minimalDatePreset).getTime();

        filterParams.adults = filterParams.adults || 0;
        filterParams.children = filterParams.children || 0;
        filterParams.date = isOldDate ? Dates.ymd(new Date(minimalDatePreset)) : filterParams.date;
        filterParams.activity_ids = filterParams.activity_ids || [];
        filterParams.groupType = filterParams.groupType || [];
        filterParams.hours_count = filterParams.hours_count || 0;

        dispatch(BookingAction.setFilter(filterParams));

        return filterParams;
    };

    return {
        changeQueryParams,
        changeQueryParamsIfUpdated,
        fetchPackagesByLocation,
        fetchPackagesForHall,
        setFilterFromQueryParams,
    };
};
